import React, { useCallback, useEffect, useMemo, useState } from 'react';
import http from '../services/httpClient';
import { formatQuantity } from '../utils/unitDisplay';
import { formatLongDate } from './ClosingDatePicker';
import './ContinuousStockPanel.css';

/**
 * Continuous Closing Stock — months, counts, item history and the switch
 * (spec §4.6, §5.3, §7.1, §9, §11; plan E2, E4, F1, F2, G1).
 *
 *   Months        Open / Count pending / Closed / Locked; the owner locks a Closed month
 *                 and reopens a Locked one with a reason; "Changes after count" lists every
 *                 late entry and the automatic offset it caused — who, when, how much.
 *   Counts        the counts applied as a difference. A manager or the owner can undo one
 *                 (reason required, previewed first) or correct one wrong line (K7).
 *   Item history  every recorded change of one item with a running balance.
 *   Switch        (Invexis staff) the business's switch and its preconditions.
 */

const STATUS = {
  open: { label: 'Open', cls: 'csp-st-open' },
  count_pending: { label: 'Count pending', cls: 'csp-st-pending' },
  closed: { label: 'Closed', cls: 'csp-st-closed' },
  locked: { label: 'Locked', cls: 'csp-st-locked' },
};
const KIND = {
  purchase: 'Bill', sale: 'Sale', online_sale: 'Online sale', table_sale: 'Table sale', wastage: 'Wastage',
  banquet: 'Banquet', complimentary: 'Complimentary', stock_out: 'Stock-out', undo: 'Undo',
  count_adjustment: 'Count', auto_offset: 'Automatic offset', unit_change: 'Unit change', opening: 'Opening', other: 'Other',
};
const fmt = (q, u) => { try { return formatQuantity(Number(q) || 0, u || 'g'); } catch (_) { return `${q} ${u || ''}`; } };
const when = (t) => (t ? new Date(t).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const errorOf = (e) => (e && e.data && e.data.error) || (e && e.message) || 'Something went wrong.';

function EffectTable({ effect }) {
  if (!effect || !effect.length) return <div className="csp-muted">Nothing changes.</div>;
  return (
    <table className="csp-table">
      <thead><tr><th>Item</th><th className="num">Recorded</th><th className="num">Offset</th><th className="num">Stock now</th></tr></thead>
      <tbody>
        {effect.map((e) => (
          <tr key={e.item_id}>
            <td>{e.item_name}</td>
            <td className="num">{fmt(e.recorded, e.unit)}</td>
            <td className="num">{e.offset ? `${fmt(e.offset, e.unit)}${e.offset_dates ? ` (${e.offset_dates})` : ''}` : '—'}</td>
            <td className="num">{Math.abs(e.change_now) > 0.00005 ? fmt(e.change_now, e.unit) : 'unchanged'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MonthsTab({ you }) {
  const [months, setMonths] = useState(null);
  const [error, setError] = useState('');
  const [changes, setChanges] = useState(null);
  const [reopen, setReopen] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const r = await http.get('/ccs/months'); setMonths(r.months || []); } catch (e) { setError(errorOf(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setBusy(true); setError('');
    try { const r = await fn(); if (r && r.success === false) setError(r.error || 'Refused.'); await load(); } catch (e) { setError(errorOf(e)); } finally { setBusy(false); }
  };

  if (changes) {
    return (
      <div>
        <button type="button" className="csp-link" onClick={() => setChanges(null)}>‹ Months</button>
        <div className="csp-h">Changes after the {changes.month} count</div>
        {changes.count
          ? <div className="csp-muted">Count dated {formatLongDate(changes.count.count_date)}, applied {when(changes.count.applied_at)}. Late entries keep the counted stock true; their offsets move the month&apos;s purchases, consumption and leakage instead.</div>
          : <div className="csp-muted">No count closes this month yet.</div>}
        {changes.changes.length === 0 && changes.count && <div className="csp-empty">No changes since the count.</div>}
        {changes.changes.length > 0 && (
          <table className="csp-table">
            <thead><tr><th>Dated</th><th>Item</th><th>What</th><th className="num">Qty</th><th>Entered</th></tr></thead>
            <tbody>
              {changes.changes.map((c) => (
                <tr key={c.entry_id} className={c.kind === 'auto_offset' ? 'csp-offset' : ''}>
                  <td>{c.business_date}</td>
                  <td>{c.item_name}</td>
                  <td>{KIND[c.kind] || c.kind}{c.note ? <span className="csp-muted"> · {c.note}</span> : null}</td>
                  <td className="num">{fmt(c.unit_qty, c.unit)}</td>
                  <td>{c.entered_by || '—'} · {when(c.entered_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  return (
    <div>
      {error && <div className="csp-error">{error}</div>}
      {!months ? <div className="csp-muted">Loading…</div> : (
        <table className="csp-table">
          <thead><tr><th>Month</th><th>Status</th><th>Count</th><th /></tr></thead>
          <tbody>
            {months.map((m) => {
              const st = STATUS[m.status] || { label: m.status, cls: '' };
              return (
                <tr key={m.month}>
                  <td>{m.label}</td>
                  <td>
                    <span className={`csp-chip ${st.cls}`}>{st.label}</span>
                    {m.derived_closing && <span className="csp-chip csp-st-derived" title="Counted on a later day: the month's closing is derived">Derived</span>}
                    {m.derived_opening && <span className="csp-chip csp-st-derived" title="Onboarded mid-month: the opening is derived and this month's leakage is indicative">Derived opening</span>}
                    {m.status === 'locked' && m.locked_by && <div className="csp-muted csp-small">by {m.locked_by}</div>}
                    {m.reopen_reason && <div className="csp-muted csp-small">reopened: {m.reopen_reason}</div>}
                  </td>
                  <td>{m.count_date ? formatLongDate(m.count_date) : '—'}</td>
                  <td className="csp-actions">
                    {(m.status === 'closed' || m.status === 'locked') && (
                      <button type="button" className="csp-btn" disabled={busy}
                        onClick={() => act(async () => { const r = await http.get(`/ccs/months/${m.month}/changes`); setChanges(r); return r; })}>
                        Changes after count
                      </button>
                    )}
                    {you.is_owner && m.status === 'closed' && (
                      <button type="button" className="csp-btn" disabled={busy}
                        onClick={() => { if (window.confirm(`Lock ${m.label}? Entries dated in it will be refused until you reopen it.`)) act(() => http.post(`/ccs/months/${m.month}/lock`, {})); }}>
                        Lock
                      </button>
                    )}
                    {you.is_owner && m.status === 'locked' && (
                      <button type="button" className="csp-btn" disabled={busy} onClick={() => setReopen({ month: m.month, label: m.label, reason: '' })}>
                        Reopen
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {reopen && (
        <div className="csp-box">
          <div className="csp-h">Reopen {reopen.label}</div>
          <textarea className="csp-input" rows={2} placeholder="Why is it being reopened?" value={reopen.reason}
            onChange={(e) => setReopen({ ...reopen, reason: e.target.value })} />
          <div className="csp-row">
            <button type="button" className="cs-modal-cancel" onClick={() => setReopen(null)}>Cancel</button>
            <button type="button" className="cs-modal-confirm" disabled={busy || reopen.reason.trim().length < 5}
              onClick={() => act(async () => { const r = await http.post(`/ccs/months/${reopen.month}/reopen`, { reason: reopen.reason }); setReopen(null); return r; })}>
              Reopen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CountsTab({ you, onChanged }) {
  const [counts, setCounts] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);      // { history, lines }
  const [undo, setUndo] = useState(null);      // { history, reason, preview }
  const [edit, setEdit] = useState(null);      // { history, line, qty, preview }
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const r = await http.get('/ccs/counts'); setCounts(r.counts || []); } catch (e) { setError(errorOf(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (path, body, dryRun) => {
    setBusy(true); setError('');
    try {
      const r = await http.post(path, { ...body, dryRun });
      if (r && r.success === false) { setError(r.error || 'Refused.'); return null; }
      return r;
    } catch (e) { setError(errorOf(e)); return null; } finally { setBusy(false); }
  };

  return (
    <div>
      {error && <div className="csp-error">{error}</div>}
      {!counts ? <div className="csp-muted">Loading…</div> : counts.length === 0 ? <div className="csp-empty">No count has been applied as a difference yet.</div> : (
        <table className="csp-table">
          <thead><tr><th>Closing stock for</th><th>By</th><th className="num">Lines</th><th /></tr></thead>
          <tbody>
            {counts.map((c) => (
              <tr key={c.history_id} className={c.undone_at ? 'csp-undone' : ''}>
                <td>
                  {formatLongDate(c.closing_date)}
                  {c.count_date !== c.closing_date && <div className="csp-muted csp-small">counted {formatLongDate(c.count_date)}</div>}
                  {c.undone_at && <div className="csp-muted csp-small">undone by {c.undone_by || '—'}: {c.undo_reason}</div>}
                </td>
                <td>{c.submitted_by || '—'}<div className="csp-muted csp-small">{when(c.submitted_at)}</div></td>
                <td className="num">{c.lines}</td>
                <td className="csp-actions">
                  <button type="button" className="csp-btn" disabled={busy}
                    onClick={async () => { const r = await http.get(`/ccs/counts/${c.history_id}/lines`); setOpen({ history: c, lines: r.lines || [] }); }}>
                    Lines
                  </button>
                  {you.is_manager && !c.undone_at && (
                    <button type="button" className="csp-btn csp-btn-danger" disabled={busy} onClick={() => setUndo({ history: c, reason: '', preview: null })}>
                      Undo
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {undo && (
        <div className="csp-box">
          <div className="csp-h">Undo the count for {formatLongDate(undo.history.closing_date)}</div>
          <div className="csp-muted">Records the exact opposite of what the count did, on its date. Everything entered after it stays. If a later count exists, current stock does not move — the difference shifts between the months.</div>
          <textarea className="csp-input" rows={2} placeholder="Why is it being undone?" value={undo.reason}
            onChange={(e) => setUndo({ ...undo, reason: e.target.value, preview: null })} />
          {undo.preview && <EffectTable effect={undo.preview.effect} />}
          <div className="csp-row">
            <button type="button" className="cs-modal-cancel" onClick={() => setUndo(null)}>Cancel</button>
            {!undo.preview ? (
              <button type="button" className="cs-modal-confirm" disabled={busy || undo.reason.trim().length < 3}
                onClick={async () => { const r = await run('/closing-stock/undo-count', { historyId: undo.history.history_id, reason: undo.reason }, true); if (r) setUndo({ ...undo, preview: r }); }}>
                Preview
              </button>
            ) : (
              <button type="button" className="cs-modal-confirm" disabled={busy}
                onClick={async () => { const r = await run('/closing-stock/undo-count', { historyId: undo.history.history_id, reason: undo.reason }, false); if (r) { setUndo(null); setOpen(null); await load(); onChanged && onChanged(); } }}>
                Undo the count
              </button>
            )}
          </div>
        </div>
      )}

      {open && (
        <div className="csp-box">
          <div className="csp-row csp-spread">
            <div className="csp-h">Lines of {formatLongDate(open.history.closing_date)}</div>
            <button type="button" className="csp-link" onClick={() => { setOpen(null); setEdit(null); }}>Close</button>
          </div>
          <table className="csp-table">
            <thead><tr><th>Item</th><th className="num">Expected</th><th className="num">Counted</th><th className="num">Difference</th><th /></tr></thead>
            <tbody>
              {open.lines.map((l) => (
                <tr key={l.item_id} className={l.replaced_at || l.undone_at ? 'csp-undone' : ''}>
                  <td>{l.item_name}{l.line_state === 'zero' && <span className="csp-chip">zero</span>}</td>
                  <td className="num">{fmt(l.expected_qty, l.unit)}</td>
                  <td className="num">{fmt(l.counted_qty, l.unit)}</td>
                  <td className="num">{fmt(l.difference_qty, l.unit)}</td>
                  <td className="csp-actions">
                    {you.is_manager && !open.history.undone_at && !l.replaced_at && !l.undone_at && (
                      <button type="button" className="csp-btn" onClick={() => setEdit({ history: open.history, line: l, qty: String(l.counted_qty), preview: null })}>
                        Correct
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {edit && (
            <div className="csp-box csp-inner">
              <div className="csp-h">Correct {edit.line.item_name}</div>
              <div className="csp-row">
                <span className="csp-muted">Counted ({edit.line.unit}):</span>
                <input className="csp-input csp-qty" inputMode="decimal" value={edit.qty}
                  onChange={(e) => setEdit({ ...edit, qty: e.target.value.replace(/[^\d.]/g, ''), preview: null })} />
              </div>
              {edit.preview && <EffectTable effect={edit.preview.effect} />}
              <div className="csp-row">
                <button type="button" className="cs-modal-cancel" onClick={() => setEdit(null)}>Cancel</button>
                {!edit.preview ? (
                  <button type="button" className="cs-modal-confirm" disabled={busy || edit.qty === ''}
                    onClick={async () => { const r = await run('/closing-stock/edit-count-line', { historyId: edit.history.history_id, itemId: edit.line.item_id, quantity: Number(edit.qty) }, true); if (r) setEdit({ ...edit, preview: r }); }}>
                    Preview
                  </button>
                ) : (
                  <button type="button" className="cs-modal-confirm" disabled={busy}
                    onClick={async () => {
                      const r = await run('/closing-stock/edit-count-line', { historyId: edit.history.history_id, itemId: edit.line.item_id, quantity: Number(edit.qty) }, false);
                      if (r) { const lr = await http.get(`/ccs/counts/${edit.history.history_id}/lines`); setOpen({ ...open, lines: lr.lines || [] }); setEdit(null); onChanged && onChanged(); }
                    }}>
                    Save the correction
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HistoryTab({ items }) {
  const [q, setQ] = useState('');
  const [hist, setHist] = useState(null);
  const [error, setError] = useState('');
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (t.length < 2) return [];
    return items.filter((i) => String(i.name || '').toLowerCase().includes(t)).slice(0, 12);
  }, [q, items]);
  const pick = async (id) => {
    setError('');
    try { setHist(await http.get(`/ccs/items/${id}/history`)); } catch (e) { setError(errorOf(e)); }
  };
  return (
    <div>
      <input className="csp-input" placeholder="Find an item…" value={q} onChange={(e) => { setQ(e.target.value); setHist(null); }} />
      {!hist && matches.length > 0 && (
        <ul className="csp-picklist">
          {matches.map((m) => <li key={m.item_id}><button type="button" onClick={() => pick(m.item_id)}>{m.name}</button></li>)}
        </ul>
      )}
      {error && <div className="csp-error">{error}</div>}
      {hist && (
        <>
          <div className="csp-h">{hist.item.name} <span className="csp-muted">({hist.item.unit})</span></div>
          <table className="csp-table">
            <thead><tr><th>Dated</th><th>What</th><th className="num">Change</th><th className="num">Balance</th><th>Entered</th></tr></thead>
            <tbody>
              {[...hist.entries].reverse().map((e) => (
                <tr key={e.entry_id} className={e.kind === 'auto_offset' ? 'csp-offset' : ''}>
                  <td>{e.business_date}{e.missing_date && <span className="csp-chip" title="The screen did not say which day this belongs to">no date</span>}</td>
                  <td>{KIND[e.kind] || e.kind}{e.note ? <span className="csp-muted"> · {e.note}</span> : null}</td>
                  <td className="num">{fmt(e.qty, hist.item.unit)}</td>
                  <td className="num">{fmt(e.balance, hist.item.unit)}</td>
                  <td className="csp-small">{e.entered_by || '—'} · {when(e.entered_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function SwitchTab() {
  const [st, setSt] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setSt(await http.get('/ccs/switch')); } catch (e) { setError(errorOf(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const flip = async (on) => {
    const msg = on
      ? 'Switch this business ON? Counts will be applied as a difference, entries never freeze, and current stock is set to the batch total for every item.'
      : 'Switch this business OFF? Stock stays as it is; counts go back to replacing stock.';
    if (!window.confirm(msg)) return;
    setBusy(true); setError('');
    try {
      const r = await http.post(on ? '/ccs/switch/on' : '/ccs/switch/off', {});
      if (r && r.success === false) setError(r.error || 'Preconditions not met.');
      await load();
    } catch (e) { setError(errorOf(e)); await load(); } finally { setBusy(false); }
  };
  if (!st) return <div className="csp-muted">{error || 'Loading…'}</div>;
  const on = !!(st.settings && st.settings.continuous_on);
  return (
    <div>
      {error && <div className="csp-error">{error}</div>}
      <div className="csp-h">Continuous closing stock is <span className={on ? 'csp-on' : 'csp-off'}>{on ? 'ON' : 'OFF'}</span></div>
      <ul className="csp-checks">
        {st.preconditions.checks.map((c) => (
          <li key={c.key} className={c.ok ? 'ok' : 'bad'}>{c.ok ? '✓' : '✗'} {c.key.replace(/_/g, ' ')} — {c.detail}</li>
        ))}
      </ul>
      <div className="csp-muted csp-small">
        Last 7 days: {st.preconditions.info.entries_7d} register entries, {st.preconditions.info.missing_date_pct_7d}% without a date.
        Switching on changes current stock for {st.preconditions.info.items_where_current_stock_will_change} item(s) to the batch total.
      </div>
      <div className="csp-row">
        {!on && <button type="button" className="cs-modal-confirm" disabled={busy || !st.preconditions.ok} onClick={() => flip(true)}>Switch on</button>}
        {on && <button type="button" className="cs-modal-cancel" disabled={busy} onClick={() => flip(false)}>Switch off</button>}
      </div>
    </div>
  );
}

export default function ContinuousStockPanel({ open, onClose, items = [], onChanged }) {
  const [tab, setTab] = useState('months');
  const [you, setYou] = useState(null);
  const [continuous, setContinuous] = useState(false);
  useEffect(() => {
    if (!open) return;
    http.get('/ccs/status').then((r) => { setYou(r.you || {}); setContinuous(!!r.continuous); }).catch(() => setYou({}));
  }, [open]);
  if (!open) return null;
  const tabs = [
    ['months', 'Months'], ['counts', 'Counts'], ['history', 'Item history'],
    ...(you && you.is_invexis_admin ? [['switch', 'Switch']] : []),
  ];
  return (
    <div className="cs-modal-overlay csp-overlay" role="dialog" aria-modal="true" aria-labelledby="csp-title">
      <div className="csp-modal">
        <div className="csp-row csp-spread">
          <div className="csp-title" id="csp-title">Stock months &amp; counts</div>
          <button type="button" className="csp-link" onClick={onClose} aria-label="Close">Close</button>
        </div>
        {!continuous && <div className="csp-muted csp-small">This business is not using continuous closing stock yet.</div>}
        <div className="csp-tabs">
          {tabs.map(([k, label]) => (
            <button key={k} type="button" className={`csp-tab${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>{label}</button>
          ))}
        </div>
        <div className="csp-body">
          {!you ? <div className="csp-muted">Loading…</div> : (
            <>
              {tab === 'months' && <MonthsTab you={you} />}
              {tab === 'counts' && <CountsTab you={you} onChanged={onChanged} />}
              {tab === 'history' && <HistoryTab items={items} />}
              {tab === 'switch' && <SwitchTab />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
